/**
 * The apps installed in this node itself (GET /api/apps/node): what the nav
 * bar lists for everyone signed in, and what the node settings' Apps tab
 * manages. Installing one is a superadmin's decision (backend
 * routers/apps.py, install_app_in_node); using one is open to everyone here.
 *
 * Fetched once per page load and shared across consumers, like use_node_info,
 * but refreshable: refresh() after an install or uninstall updates every
 * mounted consumer, so the nav bar follows the Apps tab at once.
 *
 *   const { apps, ready, refresh } = useNodeApps();
 */

import { useCallback, useEffect, useState } from 'react';
import { useApi } from './use_api';

let cached = null;          // null until the first fetch settles, then the list
let inflight = null;        // the request in progress, so mounting twice fetches once
const listeners = new Set();

function fetchNodeApps(api) {
    if (!inflight) {
        inflight = api.get('/api/apps/node')
            .then(rows => (Array.isArray(rows) ? rows : []))
            // Signed out, not yet approved, or an older node without the
            // route: no node apps, rather than an error in the nav bar.
            .catch(() => cached || [])
            .then(rows => {
                cached = rows;
                listeners.forEach(listen => listen(rows));
                return rows;
            })
            .finally(() => { inflight = null; });
    }
    return inflight;
}

export function useNodeApps() {
    const api = useApi();
    const [apps, setApps] = useState(cached || []);
    const [ready, setReady] = useState(cached !== null);

    useEffect(() => {
        const listen = rows => { setApps(rows); setReady(true); };
        listeners.add(listen);
        if (cached === null) fetchNodeApps(api); else listen(cached);
        return () => { listeners.delete(listen); };
    }, [api]);

    const refresh = useCallback(() => fetchNodeApps(api), [api]);

    return { apps, ready, refresh };
}

export default useNodeApps;
